import { fetchAuthSession } from "aws-amplify/auth";
import awsmobile from "../aws-exports.js";
import { ScanCommand } from "@aws-sdk/client-dynamodb"; // Import ScanCommand
import { fromCognitoIdentityPool } from "@aws-sdk/credential-providers";
import { DynamoDBClient, BatchGetItemCommand, UpdateItemCommand, PutItemCommand, GetItemCommand } from "@aws-sdk/client-dynamodb";
import { QueryCommand } from "@aws-sdk/client-dynamodb";
import { DeleteItemCommand } from "@aws-sdk/client-dynamodb";
import data_program from "../data/data_program.js";


// ✅ Initialize DynamoDB Client
const dynamoDBClient = new DynamoDBClient({
  region: awsmobile.aws_project_region,
  credentials: async () => {
    try {
      console.log("🔍 Attempting to fetch authenticated user credentials (v6)...");
      const { credentials } = await fetchAuthSession();

      if (credentials?.accessKeyId && credentials?.secretAccessKey && credentials?.sessionToken) {
        console.log("🟢 Authenticated AWS Credentials:", credentials);
        return {
          accessKeyId: credentials.accessKeyId,
          secretAccessKey: credentials.secretAccessKey,
          sessionToken: credentials.sessionToken,
        };
      }

    } catch (error) {
      console.warn("🟡 No authenticated user. Falling back to guest access.");
    }

    console.warn("🟡 Fetching guest credentials from Cognito...");

    try {
      const guestCredentialsProvider = fromCognitoIdentityPool({
        clientConfig: { region: awsmobile.aws_project_region },
        identityPoolId: awsmobile.aws_cognito_identity_pool_id,
      });

      const guestCredentials = await guestCredentialsProvider();
      console.log("✅ Guest AWS Credentials Retrieved:", guestCredentials);

      return guestCredentials;
    } catch (error) {
      console.error("❌ Failed to fetch guest credentials from Cognito:", error);
    }

    console.error("❌ No valid credentials found. Returning empty credentials.");
    return {
      accessKeyId: "",
      secretAccessKey: "",
      sessionToken: "",
    };
  },
});

// ---- Minimal stats helpers (weekly / monthly only) -----------------

// --- Video URL helpers (embed-safe) ---
const getYouTubeId = (url) => {
  if (!url) return null;
  const m = url.match(
    /(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([a-zA-Z0-9_-]{11})/
  );
  return m ? m[1] : null;
};

const toYouTubeEmbed = (idOrUrl) => {
  const id = /^[a-zA-Z0-9_-]{11}$/.test(idOrUrl) ? idOrUrl : getYouTubeId(idOrUrl);
  return id ? `https://www.youtube.com/embed/${id}` : null;
};

const looksLikeVideoFilePath = (url) => {
  try {
    const u = new URL(url, typeof window !== "undefined" ? window.location.href : "http://localhost");
    return /\.(mp4|webm|ogg|ogv|mov|m4v|m3u8|mpd)(\?.*)?$/i.test(u.pathname);
  } catch {
    return false;
  }
};

export { toYouTubeEmbed, looksLikeVideoFilePath };

/**
 * Given a user + progress_code, read `user_progress` and build a canonical, embeddable video URL.
 * Priority: youtube_id -> youtube_short_id -> best_url -> teaser_landscape_url -> teaser_portrait_url
 * Returns { url, kind: 'youtube' | 'file' } or null if nothing usable.
 */
const resolveFromUserProgress = async (username, progress_code) => {
  try {
    const res = await dynamoDBClient.send(
      new GetItemCommand({
        TableName: "user_progress",
        Key: {
          username: { S: username },
          progress_code: { S: progress_code },
        },
        ProjectionExpression:
          "youtube_id, youtube_short_id, best_url, teaser_landscape_url, teaser_portrait_url, s3_key",
      })
    );

    const it = res.Item || {};

    // 1) Prefer YouTube IDs (cleanest embed)
    const ytId = it.youtube_id?.S || it.youtube_short_id?.S;
    const ytEmbed = ytId && toYouTubeEmbed(ytId);
    if (ytEmbed) return { url: ytEmbed, kind: "youtube" };

    // 2) Existing URLs that might already be usable
    const candidates = [
      it.best_url?.S,
      it.teaser_landscape_url?.S,
      it.teaser_portrait_url?.S,
    ].filter(Boolean);

    for (const c of candidates) {
      const embed = toYouTubeEmbed(c);
      if (embed) return { url: embed, kind: "youtube" };
      if (looksLikeVideoFilePath(c)) return { url: c, kind: "file" };
    }

    // 3) (Optional) Use s3_key if you have a known public/CloudFront base.
    // const S3_PUBLIC_BASE = "https://<your-cloudfront-domain-or-s3-public-path>/";
    // if (it.s3_key?.S) return { url: S3_PUBLIC_BASE + it.s3_key.S, kind: "file" };

    return null;
  } catch (err) {
    console.error("❌ resolveFromUserProgress failed:", err);
    return null;
  }
};


// --- TTL helpers ---
const WEEK_TTL_DAYS = 21;
const MONTH_TTL_DAYS = 90;
const nowEpoch = () => Math.floor(Date.now() / 1000);
const secondsFromNow = (days) => nowEpoch() + days * 86400;

/** ISO week string like "week2025-36" */
const isoWeekKey = (d = new Date()) => {
  const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  // Thursday of current week to get ISO week-year
  const dayNum = (date.getUTCDay() + 6) % 7;
  date.setUTCDate(date.getUTCDate() - dayNum + 3);
  const weekYear = date.getUTCFullYear();
  const firstThursday = new Date(Date.UTC(weekYear, 0, 4));
  const weekNo = 1 + Math.round(
    ((date - firstThursday) / 86400000 - 3 + ((firstThursday.getUTCDay() + 6) % 7)) / 7
  );
  return `week${weekYear}-${String(weekNo).padStart(2, "0")}`;
};

/** Month string like "month2025-09" */
const monthKey = (d = new Date()) =>
  `month${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;

const bumpStat = async (statName, amount = 1, ttlEpoch = null) => {
  const updateParts = ["statValue = if_not_exists(statValue, :z) + :inc"];
  const values = { ":z": { N: "0" }, ":inc": { N: String(amount) } };
  const names = {};

  if (ttlEpoch) {
    updateParts.push("#ttl = :ttl");        // use alias
    values[":ttl"] = { N: String(ttlEpoch) };
    names["#ttl"] = "ttl";                  // alias mapping
  }

  const params = {
    TableName: "app_statistics",
    Key: { statName: { S: statName } },
    UpdateExpression: "SET " + updateParts.join(", "),
    ExpressionAttributeValues: values,
    ...(Object.keys(names).length > 0 && { ExpressionAttributeNames: names }),
  };

  await dynamoDBClient.send(new UpdateItemCommand(params));
};
// New users (weekly/monthly)
export const bumpNewUserCounters = async (amount = 1, now = new Date()) => {
  await Promise.all([
    bumpStat(`newUsers:${isoWeekKey(now)}`, amount, secondsFromNow(WEEK_TTL_DAYS)),
    bumpStat(`newUsers:${monthKey(now)}`, amount, secondsFromNow(MONTH_TTL_DAYS)),
  ]);
};

// Unlocks per level (weekly/monthly)
const levelFromCode = (code) => {
  const n = parseInt(String(code).slice(1, 2), 10);
  return Number.isFinite(n) ? n : null;
};

// Unlocks per level (weekly/monthly + lifetime w/o TTL)
// Unlocks per level (weekly/monthly + lifetime w/o TTL)
export const bumpUnlockCounters = async (levelNumber, amount = 1, now = new Date()) => {
  const ttlWeek = secondsFromNow(WEEK_TTL_DAYS);
  const ttlMonth = secondsFromNow(MONTH_TTL_DAYS);

  await Promise.all([
    bumpStat(
      `unlocks:L${levelNumber}:${isoWeekKey(now)}`,
      amount,
      ttlWeek
    ),
    bumpStat(
      `unlocks:L${levelNumber}:${monthKey(now)}`,
      amount,
      ttlMonth
    ),
    bumpStat(`totalUnlocks:L${levelNumber}`, amount) // lifetime (no TTL)
  ]);
};

// 🆕 Sub-level unlocks (weekly/monthly only, no lifetime)
export const bumpSubLevelUnlockCounters = async (progressCode, amount = 1, now = new Date()) => {
  const ttlWeek = secondsFromNow(WEEK_TTL_DAYS);
  const ttlMonth = secondsFromNow(MONTH_TTL_DAYS);

  await Promise.all([
    bumpStat(`unlocks:${progressCode}:${isoWeekKey(now)}`, amount, ttlWeek),
    bumpStat(`unlocks:${progressCode}:${monthKey(now)}`, amount, ttlMonth),
  ]);
};

// 🆕 Check if ALL sublevels (a,b,c) for a level are completed -> bump level unlocks
const checkAndBumpFullLevelUnlock = async (username, levelNumber) => {
  // Fetch user progress for all sublevels of this level
  const subCodes = ["a", "b", "c"].map(v => `l${levelNumber}${v}`);
  const progressList = await fetchUserProgressFromDB(username);

  const goalsMet = subCodes.every(code => {
    const entry = progressList.find(p => p.progress_code === code);
    if (!entry) return false;
    const goal = findGoalForCode(code) ?? 0;
    return entry.best >= goal;
  });

  if (goalsMet) {
    await bumpUnlockCounters(levelNumber, 1);
    console.log(`🏆 Full Level ${levelNumber} unlock recorded for ${username}`);
  }
};

export const fetchAllUsernamesFromDB = async () => {
  try {
    const params = {
      TableName: "users-dev",
      // ✅ Ask for both fields
      ProjectionExpression: "username, displayName",
    };

    const command = new ScanCommand(params);
    const result = await dynamoDBClient.send(command);

    if (!result.Items) {
      console.warn("No usernames found in DynamoDB");
      return [];
    }

    // ✅ Return objects with username + displayName
    return result.Items.map((item) => ({
      username: item.username?.S || "",                 // canonical key
      displayName: item.displayName?.S || null,        // mixed-case label if present
    }));
  } catch (error) {
    console.error("Error fetching usernames:", error);
    return [];
  }
};


// ✅ Fetch Full User Profile from DynamoDB (Updated to include all fields)
export const fetchUserProfileFromDB = async (username) => {
  try {
    console.log(`🔍 Fetching user profile for: ${username}`);

    const params = {
      TableName: "users-dev",
      Key: { username: { S: username } },
    };

    const command = new GetItemCommand(params);
    const result = await dynamoDBClient.send(command);

    if (!result.Item) {
      console.warn(`⚠️ User "${username}" not found in DynamoDB.`);
      return null;
    }

    // ---- Normalize avatar fields (no remote fallbacks here) ----
    const rawAvatar = (result.Item.avatar?.S || "").split("?")[0];
    const rawAvatarOriginal = (result.Item.avatarOriginal?.S || "none").split("?")[0];

    // Treat the old remote alien as "none" so clients use the local default instantly
    const isOldRemoteAlien = rawAvatar.includes("/avatars/avatar_alien_white.jpeg");
    const avatar = rawAvatar && !isOldRemoteAlien ? rawAvatar : "none";
    const avatarOriginal = rawAvatarOriginal || "none";

    // Fetch progress once
    const progressData = await fetchUserProgressFromDB(username);

    console.log(`✅ Successfully fetched profile for: ${username}`, result.Item);
    console.log("🧠 Loaded progressData from user_progress:", progressData);

    return {
      username: result.Item.username?.S || "",
      email: result.Item.email?.S || "",
      displayName: result.Item.displayName?.S || result.Item.username?.S || "",
      completed_date: result.Item.completed_date?.S || null,
      latest_update: result.Item.latest_update?.S || null,
      journey_percentage:
        result.Item.journey_percentage?.N !== undefined
          ? Number(result.Item.journey_percentage.N)
          : null,
      avatar,          // "none" when unset/legacy S3 default
      avatarOriginal,  // "none" default
      level_category: result.Item.level_category?.S || "",
      communityUpdateFrequency: result.Item.communityUpdateFrequency?.S || "Weekly",
      favorites: result.Item.favorites?.L
        ? result.Item.favorites.L.map((entry) => entry.S)
        : [],
      unique: result.Item.unique?.L
        ? result.Item.unique.L.map((entry) => entry.S)
        : [],
      first_upload: result.Item.first_upload?.S || "",
      progressData,
    };
  } catch (error) {
    console.error(`❌ Error fetching user "${username}" from DynamoDB:`, error);
    return null;
  }
};


export const fetchUserProgressFromDB = async (username) => {
  try {
    const command = new QueryCommand({
      TableName: "user_progress",
      IndexName: "username-progress_code-index", // ✅ Use GSI here
      KeyConditionExpression: "username = :u",
      ExpressionAttributeValues: {
        ":u": { S: username },
      },
    });

    const result = await dynamoDBClient.send(command);

    return (result.Items || []).map((entry) => ({
      progress_code: entry.progress_code?.S || "",
      best: entry.best?.N !== undefined ? Number(entry.best.N) : 0,
      best_date: entry.best_date?.S || null,
      best_url: entry.best_url?.S || null,
      teaser_landscape_url: entry.teaser_landscape_url?.S || null,
      teaser_portrait_url: entry.teaser_portrait_url?.S || null,
      processing: entry.processing?.BOOL || false,
    }));
  } catch (err) {
    console.error("❌ Error fetching progress data:", err);
    return [];
  }
};

export const saveUserProfileToDB = async (username, updatedProfile) => {
  try {
    console.log(
      `📤 Saving profile for user: ${username}`,
      updatedProfile
    );

    const expressionParts = ["is_user = if_not_exists(is_user, :iu)"];
    const expressionValues = { ":iu": { S: "USER" } };

    if (updatedProfile.first_upload !== undefined) {
      expressionParts.push("first_upload = :fu");
      expressionValues[":fu"] = updatedProfile.first_upload
        ? { S: updatedProfile.first_upload }
        : { NULL: true };
    }

    if (updatedProfile.email !== undefined) {
      expressionParts.push("email = :em");
      expressionValues[":em"] = updatedProfile.email ? { S: updatedProfile.email } : { NULL: true };
    }

    if (updatedProfile.avatar !== undefined) {
      expressionParts.push("avatar = :av");
      expressionValues[":av"] = { S: updatedProfile.avatar };
    }

    if (updatedProfile.avatarOriginal !== undefined) {
      expressionParts.push("avatarOriginal = :aov");
      expressionValues[":aov"] = updatedProfile.avatarOriginal ? { S: updatedProfile.avatarOriginal } : { NULL: true };
    }

    if (updatedProfile.displayName !== undefined) {
      expressionParts.push("displayName = :dn");
      expressionValues[":dn"] = { S: updatedProfile.displayName };
    }

    if (updatedProfile.communityUpdateFrequency !== undefined) {
      expressionParts.push("communityUpdateFrequency = :cuf");
      expressionValues[":cuf"] = { S: updatedProfile.communityUpdateFrequency };
    }

    if (updatedProfile.favorites !== undefined) {
      expressionParts.push("favorites = :fav");
      expressionValues[":fav"] = {
        L: updatedProfile.favorites.map(fav => ({ S: fav }))
      };
    }

    if (updatedProfile.disclaimerAcceptedDate !== undefined) {
      expressionParts.push("disclaimerAcceptedDate = :dad");
      expressionValues[":dad"] = updatedProfile.disclaimerAcceptedDate ? { S: updatedProfile.disclaimerAcceptedDate } : { NULL: true };
    }

    if (updatedProfile.level_category !== undefined) {
      expressionParts.push("level_category = :lc");
      expressionValues[":lc"] = { S: updatedProfile.level_category || "L1" };
    }

    if (expressionParts.length === 1 && updatedProfile.favorites === undefined) {
      // Add other fields to this check if you have other mandatory update fields
      console.warn("⚠️ No new fields provided to update for user:", username);
      return;
    }

    const updateParams = {
      TableName: "users-dev",
      Key: {
        username: { S: username },
      },
      UpdateExpression: "SET " + expressionParts.join(", "),
      ExpressionAttributeValues: expressionValues,
    };

    const updateCommand = new UpdateItemCommand(updateParams);
    await dynamoDBClient.send(updateCommand);

    console.log("✅ User profile updated successfully in DynamoDB");
  } catch (error) {
    console.error("❌ Error updating user profile in DynamoDB:", error);
    throw error;
  }
};

const findGoalForCode = (progress_code) => {
  const programEntry = data_program.find(p => p.progress_code === progress_code);
  return programEntry ? programEntry.goal : null;
};

const computeLevelCategoryFromProgress = (progressData = []) => {
  const maxLevels = 7;
  let firstIncompleteLevel = -1;

  for (let levelNum = 1; levelNum <= maxLevels; levelNum++) {
    const levelTasks = data_program.filter(
      (data) => data.level === `Level ${levelNum}`
    );

    if (!levelTasks.length) continue; // no tasks defined, skip

    const levelComplete = levelTasks.every((data) => {
      const userProgress = progressData.find(
        (p) => p.progress_code === data.progress_code
      );
      const best = userProgress?.best ?? 0;
      const goal = data.goal ?? 0;

      if (!goal) return false; // no goal configured → can't be complete

      const pct = Math.min((best / goal) * 100, 100);
      return pct >= 100;
    });

    if (!levelComplete) {
      firstIncompleteLevel = levelNum;
      break;
    }
  }

  if (firstIncompleteLevel === -1) {
    // all levels complete → cap at highest level
    firstIncompleteLevel = maxLevels;
  }

  // Store like "L2"
  return `L${firstIncompleteLevel}`;
};

export const updateUserProgressInDB = async (username, progressData, journeyPercentage) => {
  try {

    const today = new Date();
    const localDateStr = today.toLocaleDateString("en-CA"); // yyyy-mm-dd (local time)
    console.log("📤 Writing first_upload to DB:", localDateStr);

    for (const entry of progressData) {
      const code = entry.progress_code;
      const goal = findGoalForCode(code) ?? 0;
      const newBest = Number(entry.best) || 0;

      const updateParams = {
        TableName: "user_progress",
        Key: {
          username: { S: username },
          progress_code: { S: code },
        },
        UpdateExpression: "SET best = :b, best_date = :bd, best_url = :bu, processing = :p",
        ExpressionAttributeValues: {
          ":b": { N: newBest.toString() },
          ":bd": { S: entry.best_date || localDateStr },
          ":bu": entry.best_url ? { S: entry.best_url } : { NULL: true },
          ":p": { BOOL: false },
        },
        ReturnValues: "ALL_OLD",
      };

      const resp = await dynamoDBClient.send(new UpdateItemCommand(updateParams));
      const oldBestAttr = resp.Attributes?.best;
      const prevBest = oldBestAttr?.N !== undefined ? Number(oldBestAttr.N) : 0;

      // Count an unlock ONLY if we just crossed the goal threshold
      if (prevBest < goal && newBest >= goal) {
        const lvl = levelFromCode(code);
        if (lvl) {
          // bump sub-level unlock
          await bumpSubLevelUnlockCounters(code, 1);
          console.log(`🏅 Sub-level unlock counted for ${username} on ${code}`);

          // after bumping a sub-level, check if full level is now completed
          await checkAndBumpFullLevelUnlock(username, lvl);
        }
      }
    }

    // 🔢 NEW: re-read full progress from DB so we never downgrade due to partial updates
    const fullProgressData = await fetchUserProgressFromDB(username);
    const newLevelCategory = computeLevelCategoryFromProgress(fullProgressData); // e.g. "L2"

    // Update metadata in users-dev (now includes level_category)
    const metaUpdateParams = {
      TableName: "users-dev",
      Key: { username: { S: username } },
      UpdateExpression:
        "SET latest_update = :lu, journey_percentage = :jp, is_user = :iu, level_category = :lc",
      ExpressionAttributeValues: {
        ":lu": { S: localDateStr },
        ":jp": { N: journeyPercentage.toString() },
        ":iu": { S: "USER" },
        ":lc": { S: newLevelCategory },
      },
    };

    console.log("🧪 Writing user metadata with params:", {
      username,
      localDateStr,
      journeyPercentage,
      newLevelCategory,
      metaUpdateParams,
    });
    await dynamoDBClient.send(new UpdateItemCommand(metaUpdateParams));
    console.log("✅ Updated user progress + metadata (incl. level_category)");
  } catch (error) {
    console.error("❌ Failed to update user progress:", error);
  }
};


export const recordFirstUploadDate = async (username) => {
  const localDateStr = new Date().toLocaleDateString("en-CA");

  try {
    const updateParams = {
      TableName: "users-dev",
      Key: { username: { S: username } },
      UpdateExpression: "SET first_upload = if_not_exists(first_upload, :fu)",
      ExpressionAttributeValues: {
        ":fu": { S: localDateStr },
      },
    };

    await dynamoDBClient.send(new UpdateItemCommand(updateParams));
    console.log(`📝 first_upload written to DB for ${username}: ${localDateStr}`);
  } catch (err) {
    console.error("❌ Failed to record first upload date:", err);
  }
};

// 🆕 Create a new user in DynamoDB if they don't already exist
export const initializeNewUserInDB = async (
  username,
  email,
  disclaimerAcceptedDate,
  awsCredentials, // 👈 pass this in from AuthContext
  options = {}
) => {
  try {
    const displayName = options.displayName || username;

    if (!awsCredentials) {
      console.error("❌ No AWS credentials provided to initializeNewUserInDB");
      return { status: "error", error: new Error("Missing AWS credentials") };
    }

    // Use *authenticated* credentials here
    const dynamoDBClient = new DynamoDBClient({
      region: "us-east-2", // <-- update if needed
      credentials: awsCredentials,
    });

    // 1. Check if the user already exists
    const checkParams = {
      TableName: "users-dev",
      Key: { username: { S: username } },
      ConsistentRead: true,
    };

    const checkCommand = new GetItemCommand(checkParams);
    const checkResult = await dynamoDBClient.send(checkCommand);

    if (checkResult.Item) {
      console.log(`🟡 User "${username}" already exists in DynamoDB`);
      return { status: "existing" };
    }

    const progressCodes = [
      "l1a", "l1b", "l1c", "l2a", "l2b", "l2c", "l3a", "l3b", "l3c",
      "l4a", "l4b", "l4c", "l5a", "l5b", "l5c", "l6a", "l6b", "l6c",
    ];

    // 2. Construct initial user item
    const putUserParams = {
      TableName: "users-dev",
      Item: {
        username: { S: username },
        displayName: { S: displayName },
        email: { S: email },
        avatar: { S: "none" }, // let the client show the bundled alien instantly
        avatarOriginal: { S: "none" },
        disclaimerAcceptedDate: { S: disclaimerAcceptedDate },
        journey_completed: { NULL: true },
        latest_update: { S: "1900-01-01T00:00:00.000000" },
        journey_percentage: { N: "0" },
        communityUpdateFrequency: { S: "Weekly" },
        favorites: { L: [] },
        unique: { L: [] },
        level_category: { S: "L1" },
        is_user: { S: "USER" },
      },
    };

    // 3. Initialize progress rows
    for (const code of progressCodes) {
      const progressParams = {
        TableName: "user_progress",
        Item: {
          username: { S: username },
          progress_code: { S: code },
          best: { N: "0" },
          best_date: { NULL: true },
          best_url: { NULL: true },
          teaser_landscape_url: { NULL: true },
          teaser_portrait_url: { NULL: true },
          processing: { BOOL: false },
        },
      };
      await dynamoDBClient.send(new PutItemCommand(progressParams));
    }

    // 4. Finally create the user record
    await dynamoDBClient.send(new PutItemCommand(putUserParams));

    console.log(`✅ New user "${username}" initialized in DynamoDB`);

    // 5. Update app statistics just like before
    await updateTotalUserCount(1);
    await bumpNewUserCounters(1);

    console.log("📤 Putting new user to DynamoDB:", {
      username,
      email,
      disclaimerAcceptedDate,
      displayName,
    });

    return { status: "new" };
  } catch (error) {
    console.error("❌ Error initializing user in DynamoDB:", error);
    return { status: "error", error };
  }
};

export const setProgressProcessingFlag = async (username, progress_code, isProcessing) => {
  try {
    const updateParams = {
      TableName: "user_progress",
      Key: {
        username: { S: username },
        progress_code: { S: progress_code },
      },
      UpdateExpression: "SET processing = :p",
      ExpressionAttributeValues: {
        ":p": { BOOL: isProcessing },
      },
    };

    await dynamoDBClient.send(new UpdateItemCommand(updateParams));
    console.log(`✅ Updated processing flag for ${username} - ${progress_code}`);
    return true;
  } catch (err) {
    console.error("❌ Error setting processing flag:", err);
    return false;
  }
};


export const fetchClosestCommunityVideos = async (targetCode, currentUser, currentProgress) => {
  const fetchVideosForCode = async (code) => {
    try {
      const response = await dynamoDBClient.send(
        new GetItemCommand({
          TableName: "progression_status",
          Key: {
            progress_code: { S: code },
          },
          ProjectionExpression: "latest_progress_videos, latest_unlocked_videos",
        })
      );

      const progressList = response.Item?.latest_progress_videos?.L || [];
      const unlockedList = response.Item?.latest_unlocked_videos?.L || [];

      const allVideos = [...progressList, ...unlockedList];

      return allVideos
        .map((entry) => {
          const obj = entry.M || {};
          return {
            username: obj.username?.S,
            url: obj.url?.S,
            date: obj.date?.S,
            code,
          };
        })
        .filter(
          (item) =>
            item.username &&
            item.username !== currentUser &&
            item.url &&
            item.url.startsWith("http")
        );
    } catch (err) {
      console.error(`❌ Failed to fetch progression_status for ${code}:`, err);
      return [];
    }
  };

  try {
    const level = parseInt(targetCode[1]);
    const variant = targetCode[2];
    const seenUrls = new Set();
    const finalResults = [];

    const codesToTry = [
      targetCode,
      ...["a", "b", "c"].filter((v) => v !== variant).map((v) => `l${level}${v}`),
      ...(level < 6 ? ["a", "b", "c"].map((v) => `l${level + 1}${v}`) : []),
      ...(level > 1 ? ["a", "b", "c"].map((v) => `l${level - 1}${v}`) : []),
    ];

    for (const code of codesToTry) {
      const videos = await fetchVideosForCode(code);
      for (const v of videos.sort((a, b) => new Date(b.date) - new Date(a.date))) {
        if (!seenUrls.has(v.url)) {
          seenUrls.add(v.url);
          finalResults.push(v);
          if (finalResults.length === 2) break;
        }
      }
      if (finalResults.length === 2) break;
    }

    const [lower, higher] = finalResults;

    return {
      lower: lower || null,
      higher: higher || null,
    };
  } catch (err) {
    console.error("❌ Failed to fetch related videos:", err);
    return { lower: null, higher: null };
  }
};

export const deleteUserFromDB = async (username) => {
  try {
    console.log(`🗑️ Starting deletion for user: ${username}`);

    //
    // STEP 0 — decrement FIRST (prevents lost decrements)
    //
    try {
      await updateTotalUserCount(-1);
      console.log("📉 Decremented total user count by 1");
    } catch (statErr) {
      console.error("⚠️ Failed to update totalUserCount:", statErr);
      // IMPORTANT: do NOT return — still delete user
    }

    //
    // STEP 1 — delete from users-dev
    //
    const profileDeleteParams = {
      TableName: "users-dev",
      Key: { username: { S: username } },
    };

    await dynamoDBClient.send(new DeleteItemCommand(profileDeleteParams));
    console.log("🧼 Deleted from users-dev");

    //
    // STEP 2 — query all user_progress entries
    //
    const queryCommand = new QueryCommand({
      TableName: "user_progress",
      IndexName: "username-progress_code-index",
      KeyConditionExpression: "username = :u",
      ExpressionAttributeValues: {
        ":u": { S: username },
      },
    });

    const result = await dynamoDBClient.send(queryCommand);
    const items = result.Items || [];

    if (!items.length) {
      console.warn("⚠️ No user_progress entries found for this user.");
    }

    //
    // STEP 3 — delete progress entries
    //
    for (const item of items) {
      const deleteParams = {
        TableName: "user_progress",
        Key: {
          username: { S: username },
          progress_code: { S: item.progress_code.S },
        },
      };

      await dynamoDBClient.send(new DeleteItemCommand(deleteParams));
      console.log(`🧼 Deleted user_progress entry: ${item.progress_code.S}`);
    }

    console.log(`✅ All data deleted for user: ${username}`);
  } catch (err) {
    console.error("❌ Error during user deletion:", err);
  }
};


export const checkProgressProcessingStatus = async (username, progress_code) => {
  try {
    const params = {
      TableName: "user_progress",
      Key: {
        username: { S: username },
        progress_code: { S: progress_code },
      },
    };

    const command = new GetItemCommand(params);
    const result = await dynamoDBClient.send(command);

    if (!result.Item) {
      console.warn(`⚠️ Progress entry not found for ${username} - ${progress_code}`);
      return true; // Assume still processing
    }

    return result.Item.processing?.BOOL ?? true;
  } catch (err) {
    console.error("❌ Error checking processing status:", err);
    return true; // Fail-safe: assume still processing
  }
};

export const fetchProgressionStatus = async (progress_code) => {
  try {
    const params = {
      TableName: "progression_status",
      Key: {
        progress_code: { S: progress_code },
      },
    };
    const command = new GetItemCommand(params);
    const result = await dynamoDBClient.send(command);

    if (!result.Item) {
      console.warn(`⚠️ Progression status not found for code: ${progress_code}`);
      return { unlockedVideos: [], progressVideos: [] };
    }

    const unlockedVideos = (result.Item.latest_unlocked_videos?.L || []).map(v => ({
      url: v.M.url.S,
      username: v.M.username.S,
      date: v.M.date.S
    }));

    const progressVideos = (result.Item.latest_progress_videos?.L || []).map(v => ({
      url: v.M.url.S,
      username: v.M.username.S,
      date: v.M.date.S
    }));

    return { unlockedVideos, progressVideos };
  } catch (error) {
    console.error(`❌ Failed to fetch progression_status for ${progress_code}`, error);
    return { unlockedVideos: [], progressVideos: [] };
  }
};


export const fetchPrioritizedVideos = async (progress_code) => {
  const { unlockedVideos, progressVideos } = await fetchProgressionStatus(progress_code);

  // ✅ Tag each list before merging
  const taggedUnlocked = (unlockedVideos || []).map(v => ({ ...v, source: 'unlocked' }));
  const taggedProgress = (progressVideos || []).map(v => ({ ...v, source: 'progress' }));

  let prioritizedVideos = [...taggedUnlocked, ...taggedProgress];

  try {
    // Keep Zarrar prioritization (unlocked if he reached goal)
    const progressList = await fetchUserProgressFromDB('zarrar');
    const zarrarProgress = progressList.find(p => p.progress_code === progress_code);

    if (zarrarProgress) {
      const goal = findGoalForCode(progress_code);
      if (goal !== null && zarrarProgress.best >= goal) {
        const url = zarrarProgress.best_url || zarrarProgress.teaser_landscape_url || zarrarProgress.teaser_portrait_url;

        if (url) {
          const zarrarVideo = {
            url,
            username: 'zarrar',
            date: zarrarProgress.best_date || new Date().toISOString(),
            source: 'unlocked', // 👈 important
          };
          prioritizedVideos = prioritizedVideos.filter(v => v.username.toLowerCase() !== 'zarrar');
          prioritizedVideos = [zarrarVideo, ...prioritizedVideos];
        }
      }
    }
  } catch (err) {
    console.error('❌ Failed to fetch Zarrar priority video', err);
  }

  // 🔧 Resolve to canonical, embeddable URLs (preserve source)
  const upgraded = await Promise.all(
    prioritizedVideos.map(async (v) => {
      const canonical = await resolveFromUserProgress(v.username, progress_code);
      if (canonical) return { ...v, url: canonical.url, kind: canonical.kind };
      const ytEmbed = toYouTubeEmbed(v.url);
      if (ytEmbed) return { ...v, url: ytEmbed, kind: "youtube" };
      if (looksLikeVideoFilePath(v.url)) return { ...v, kind: "file" };
      return null;
    })
  );

  const seen = new Set();
  return upgraded
    .filter(Boolean)
    .filter(v => (seen.has(v.url) ? false : (seen.add(v.url), true)));
};


// 🆕 NEW, SAFE FUNCTION: To read users from the new GSIs
export const fetchUsersByGSI = async ({
  sortOption = "Latest",
  selectedLevel = null,
  usersPerPage = 5,
  lastEvaluatedKey = null,
}) => {
  const params = {
    TableName: "users-dev",
    Limit: usersPerPage,
    ScanIndexForward: false, // Always sort descending (latest date, greatest percentage)
  };

  if (lastEvaluatedKey) {
    params.ExclusiveStartKey = lastEvaluatedKey;
  }

  // Determine which index to use based on the filters
  if (selectedLevel) {
    params.IndexName = "Level-Journey-Index";
    params.KeyConditionExpression = "level_category = :level";
    params.ExpressionAttributeValues = {
      ":level": { S: `L${selectedLevel}` },
    };
  } else if (sortOption === "Greatest") {
    params.IndexName = "GreatestProgressIndex";
    params.KeyConditionExpression = "is_user = :is_user";
    params.ExpressionAttributeValues = { ":is_user": { S: "USER" } };
  } else {
    // Default to 'Latest'
    params.IndexName = "LatestUpdateIndex";
    params.KeyConditionExpression = "is_user = :is_user";
    params.ExpressionAttributeValues = { ":is_user": { S: "USER" } };
  }

  try {
    const command = new QueryCommand(params);
    const result = await dynamoDBClient.send(command);

    // This function will return the raw items from the GSI.
    // The component will then fetch the detailed progress for each user.
    const users = (result.Items || []).map((item) => {
      // This is a lightweight mapping. We'll get full progress later.
      return {
        username: item.username?.S || "",
        displayName: item.displayName?.S || item.username?.S || "",  // 👈 NEW
        avatar: item.avatar?.S || "",
        journey_percentage: item.journey_percentage?.N ? Number(item.journey_percentage.N) : 0,
        level_category: item.level_category?.S || "",
        first_upload: item.first_upload?.S || "",
        latest_update: item.latest_update?.S || null, // ✅ ADD THIS LINE
        // We are NOT fetching progressData here to keep this function fast
      };
    });

    return {
      users,
      lastEvaluatedKey: result.LastEvaluatedKey,
    };
  } catch (error) {
    console.error("Error fetching users with GSI:", error);
    return { users: [], lastEvaluatedKey: null };
  }
};


/**
 * Updates the global total user count in the app_statistics table.
 * @param {number} amount - The amount to add (e.g., 1 for a new user).
 */
export const updateTotalUserCount = async (amount) => {
  const params = {
    TableName: "app_statistics",
    Key: { statName: { S: "totalUserCount" } },
    UpdateExpression: "SET statValue = if_not_exists(statValue, :zero) + :inc",
    ExpressionAttributeValues: {
      ":inc": { N: String(amount) },
      ":zero": { N: "0" },
    },
  };
  try {
    await dynamoDBClient.send(new UpdateItemCommand(params));
    console.log(`✅ Updated totalUserCount by ${amount}`);
  } catch (error) {
    console.error("Error updating total user count:", error);
  }
};

/**
 * Fetches the global total user count from the app_statistics table.
 */
export const fetchTotalUserCount = async () => {
  const params = {
    TableName: "app_statistics",
    Key: { statName: { S: "totalUserCount" } },
  };
  try {
    const result = await dynamoDBClient.send(new GetItemCommand(params));
    return result.Item ? Number(result.Item.statValue.N) : 0;
  } catch (error) {
    console.error("Error fetching total user count:", error);
    return 0;
  }
};


/**
 * Gets the total count of users for a specific level category using the GSI.
 * This is very fast and efficient.
 * @param {number} levelNumber - The level to get a count for (e.g., 3).
 * @returns {Promise<number>} The total count of users in that level.
 */
export const getCountOfUsersByLevel = async (levelNumber) => {
  const params = {
    TableName: "users-dev",
    IndexName: "Level-Journey-Index",
    KeyConditionExpression: "level_category = :level",
    ExpressionAttributeValues: {
      ":level": { S: `L${levelNumber}` },
    },
    Select: "COUNT", // This is the key: only returns the count, not the data.
  };

  try {
    const command = new QueryCommand(params);
    const result = await dynamoDBClient.send(command);
    console.log(`[Count] Found ${result.Count} users for Level ${levelNumber}.`);
    return result.Count || 0;
  } catch (error) {
    console.error(`❌ Error counting users for Level ${levelNumber}:`, error);
    return 0;
  }
};


export const fetchUsersByUsernames = async (usernames) => {
  if (!usernames || usernames.length === 0) {
    return { users: [] };
  }

  // DynamoDB BatchGetItem can only handle 100 items at a time.
  // We'll handle this just in case, though it's unlikely a user has > 100 favorites.
  if (usernames.length > 100) {
    console.warn("Cannot fetch more than 100 users at a time.");
    usernames = usernames.slice(0, 100);
  }

  const params = {
    RequestItems: {
      "users-dev": { // Your table name
        Keys: usernames.map(username => ({ "username": { S: username } })),
      },
    },
  };

  try {
    const command = new BatchGetItemCommand(params);
    const result = await dynamoDBClient.send(command);

    const usersData = result.Responses["users-dev"] || [];

    // The component expects progressData, so we fetch it for each user
    const usersWithProgress = await Promise.all(
      usersData.map(async (item) => {
        const user = {
          username: item.username?.S || "",
          displayName: item.displayName?.S || item.username?.S || "",
          avatar: item.avatar?.S || "",
          journey_percentage: item.journey_percentage?.N ? Number(item.journey_percentage.N) : 0,
          level_category: item.level_category?.S || "",
          first_upload: item.first_upload?.S || "",
          latest_update: item.latest_update?.S || null,
        };
        user.progressData = await fetchUserProgressFromDB(user.username);
        return user;
      })
    );

    return { users: usersWithProgress };
  } catch (error) {
    console.error("Error fetching users by username:", error);
    return { users: [] };
  }
};

export const saveUserCommentToProgress = async (username, progress_code, comment) => {
  try {

    const updateParams = {
      TableName: "user_progress",
      Key: {
        username: { S: username },
        progress_code: { S: progress_code },
      },
      UpdateExpression: "SET best_comment = :c",
      ExpressionAttributeValues: {
        ":c": comment ? { S: comment } : { NULL: true },
      },
    };

    console.log("🧾 UpdateItem params:", updateParams);

    await dynamoDBClient.send(new UpdateItemCommand(updateParams));
    console.log(`✅ Saved comment for ${username} - ${progress_code}`);
  } catch (error) {
    console.error("❌ Error saving best_comment:", error);
  }
};

export { checkAndBumpFullLevelUnlock };

/**
 * Saves or updates S3 and social media post IDs for a specific progress code.
 * Called after upload completes for any platform.
 */
export const saveVideoPostIdsToDB = async (username, progress_code, ids = {}) => {
  try {
    const { s3_key, youtube_id, youtube_short_id, facebook_id, instagram_id, tiktok_id } = ids;

    const updates = [];
    const values = {};

    if (s3_key !== undefined) {
      updates.push("s3_key = :s3");
      values[":s3"] = s3_key ? { S: s3_key } : { NULL: true };
    }
    if (youtube_id !== undefined) {
      updates.push("youtube_id = :yt");
      values[":yt"] = youtube_id ? { S: youtube_id } : { NULL: true };
    }
    if (youtube_short_id !== undefined) {
      updates.push("youtube_short_id = :ytshort");
      values[":ytshort"] = youtube_short_id ? { S: youtube_short_id } : { NULL: true };
    }
    if (facebook_id !== undefined) {
      updates.push("facebook_id = :fb");
      values[":fb"] = facebook_id ? { S: facebook_id } : { NULL: true };
    }
    if (instagram_id !== undefined) {
      updates.push("instagram_id = :ig");
      values[":ig"] = instagram_id ? { S: instagram_id } : { NULL: true };
    }
    if (tiktok_id !== undefined) {
      updates.push("tiktok_id = :tk");
      values[":tk"] = tiktok_id ? { S: tiktok_id } : { NULL: true };
    }

    if (updates.length === 0) {
      console.warn("⚠️ No IDs provided to save for", username, progress_code);
      return;
    }

    const params = {
      TableName: "user_progress",
      Key: {
        username: { S: username },
        progress_code: { S: progress_code },
      },
      UpdateExpression: "SET " + updates.join(", "),
      ExpressionAttributeValues: values,
    };

    await dynamoDBClient.send(new UpdateItemCommand(params));
    console.log(`✅ Saved post IDs for ${username}:${progress_code}`, ids);
  } catch (error) {
    console.error("❌ Error saving post IDs:", error);
  }
};
